"use client";

import { useId } from "react";
import { ActivityScenePreview } from "./reference-preview-primitives";

export type SwimmingDisplayPreviewProps = {
  participants: {
    id: string;
    displayName: string;
    avatarUrl: string | null;
    username: string | null;
  }[];
};

/*
 * The overhead race shot: sunlit turquoise water with caustics rippling over
 * the pool floor, bead lane ropes (red near the wall), lane lines with their
 * T-ends, the tiled finish wall on the right, and a swimmer mid-stroke in the
 * middle lane (the focal point) leading a second one a lane down, both
 * throwing foam and a wake. The bottom lane runs under the participant pill.
 *
 * Motion: the caustic layer drifts one tile and loops; glints twinkle — CSS
 * transform and opacity only, off under reduced motion.
 */
const SWIM_KEYFRAMES = `
@keyframes swim-caustics {
  0% { transform: translate3d(0, 0, 0) }
  100% { transform: translate3d(64px, 48px, 0) }
}
@keyframes swim-glint {
  0%, 100% { opacity: 0; transform: scale(0.4) }
  50% { opacity: 1; transform: scale(1) }
}
.swim-caustics { animation: swim-caustics 14s linear infinite }
.swim-glint { animation: swim-glint 2.6s ease-in-out infinite; transform-box: fill-box; transform-origin: center }
@media (prefers-reduced-motion: reduce) {
  .swim-caustics { animation: none }
  .swim-glint { animation: none; opacity: 0.7 }
}
`;

function useSvgIds() {
  const uid = useId().replace(/[^\w-]/g, "");
  return (name: string) => `swim-${uid}-${name}`;
}

type IdFn = ReturnType<typeof useSvgIds>;

// Fixed pixel geometry: the scene is always 220px tall.
const ROPES = [48, 104, 160];
const LANE_CENTERS = [20, 76, 132, 190];

const SKIN = "#e0a07a";

const LANE_LINE = { background: "rgba(6,57,107,0.55)" } as const;

function SceneDefs({ id }: { id: IdFn }) {
  return (
    <svg className="absolute h-0 w-0" aria-hidden="true" focusable="false">
      <defs>
        <pattern
          id={id("caustics")}
          width="64"
          height="48"
          patternUnits="userSpaceOnUse"
        >
          <path
            d="M0 10 C10 4 18 16 30 10 S50 2 64 10 M0 34 C12 28 22 40 34 34 S52 26 64 34 M14 0 C10 10 22 18 16 28 S8 40 14 48 M46 0 C42 12 54 20 48 30 S40 42 46 48"
            stroke="#e6fdff"
            strokeOpacity="0.32"
            strokeWidth="1.6"
            fill="none"
          />
        </pattern>
        <radialGradient id={id("bead-blue")} cx="0.35" cy="0.3" r="0.8">
          <stop offset="0" stopColor="#8fc4ff" />
          <stop offset="0.6" stopColor="#1f5fd6" />
          <stop offset="1" stopColor="#0c2a6e" />
        </radialGradient>
        <radialGradient id={id("bead-white")} cx="0.35" cy="0.3" r="0.8">
          <stop offset="0" stopColor="#ffffff" />
          <stop offset="1" stopColor="#b8c8d8" />
        </radialGradient>
        <radialGradient id={id("bead-red")} cx="0.35" cy="0.3" r="0.8">
          <stop offset="0" stopColor="#ff9a8f" />
          <stop offset="0.6" stopColor="#e2283a" />
          <stop offset="1" stopColor="#7a0a16" />
        </radialGradient>
        <pattern
          id={id("rope")}
          width="14"
          height="9"
          patternUnits="userSpaceOnUse"
        >
          <ellipse
            cx="3.5"
            cy="4.5"
            rx="3.3"
            ry="4.2"
            fill={`url(#${id("bead-blue")})`}
          />
          <ellipse
            cx="10.5"
            cy="4.5"
            rx="3.3"
            ry="4.2"
            fill={`url(#${id("bead-white")})`}
          />
        </pattern>
        <pattern
          id={id("rope-red")}
          width="7"
          height="9"
          patternUnits="userSpaceOnUse"
        >
          <ellipse
            cx="3.5"
            cy="4.5"
            rx="3.3"
            ry="4.2"
            fill={`url(#${id("bead-red")})`}
          />
        </pattern>
      </defs>
    </svg>
  );
}

/** Caustics + ropes, in px so nothing stretches at wide card widths. */
function Pool({ id }: { id: IdFn }) {
  return (
    <>
      <div className="absolute inset-0 overflow-hidden">
        <svg
          className="swim-caustics absolute"
          style={{
            top: -48,
            left: -64,
            width: "calc(100% + 128px)",
            height: "calc(100% + 96px)",
          }}
          aria-hidden="true"
          focusable="false"
        >
          <rect width="100%" height="100%" fill={`url(#${id("caustics")})`} />
        </svg>
      </div>
      {/* Lane lines on the floor, with T-ends at the wall */}
      {LANE_CENTERS.map((y) => (
        <div
          key={`line-${y}`}
          className="absolute -translate-y-1/2 rounded-full"
          style={{ ...LANE_LINE, top: y, left: "6%", right: 34, height: 6 }}
        >
          <div
            className="absolute top-1/2 right-0 -translate-y-1/2 rounded-full"
            style={{ ...LANE_LINE, width: 6, height: 22 }}
          />
        </div>
      ))}
      <svg
        className="absolute inset-0 h-full w-full"
        aria-hidden="true"
        focusable="false"
      >
        {ROPES.map((y) => (
          <g key={`rope-${y}`}>
            <rect
              x="0"
              y={y - 1}
              width="100%"
              height="9"
              fill="#032a4a"
              opacity="0.25"
            />
            <rect
              x="0"
              y={y - 4.5}
              width="100%"
              height="9"
              fill={`url(#${id("rope")})`}
            />
          </g>
        ))}
      </svg>
      {/* Red beads for the last 5 m before the wall */}
      <svg
        className="absolute inset-y-0 h-full"
        style={{ right: 14, width: 56 }}
        aria-hidden="true"
        focusable="false"
      >
        {ROPES.map((y) => (
          <rect
            key={`red-${y}`}
            x="0"
            y={y - 4.5}
            width="100%"
            height="9"
            fill={`url(#${id("rope-red")})`}
          />
        ))}
      </svg>
      {/* Tiled finish wall + touchpads */}
      <div
        className="absolute inset-y-0 right-0"
        style={{
          width: 16,
          background:
            "linear-gradient(90deg, #9fb6c4 0%, #e8f1f6 45%, #c7d6df 100%)",
          boxShadow: "-4px 0 10px rgba(0,40,70,0.35)",
        }}
      >
        <div
          className="absolute inset-0"
          style={{
            background:
              "repeating-linear-gradient(180deg, transparent 0 7px, rgba(60,90,110,0.35) 7px 8px)",
          }}
        />
        {LANE_CENTERS.map((y) => (
          <div
            key={`pad-${y}`}
            className="absolute -translate-y-1/2 rounded-sm"
            style={{
              top: y,
              left: -3,
              width: 5,
              height: 30,
              background: "#1b2530",
            }}
          />
        ))}
      </div>
    </>
  );
}

/** Freestyle from above, heading right; the left arm is mid-recovery. */
function Swimmer({ transform, cap }: { transform: string; cap: string }) {
  return (
    <g transform={transform}>
      {/* Shadow on the pool floor */}
      <ellipse cx="-4" cy="12" rx="40" ry="8" fill="#03315a" opacity="0.28" />
      {/* Wake */}
      <path
        d="M22 -9 Q-20 -16 -70 -22 M22 9 Q-20 16 -70 22"
        stroke="#ffffff"
        strokeOpacity="0.4"
        strokeWidth="1.2"
        fill="none"
      />
      {/* Kicking legs + foam */}
      <path
        d="M-14 -2.5 L-36 -4.5"
        stroke={SKIN}
        strokeWidth="4"
        strokeLinecap="round"
      />
      <path
        d="M-14 2.5 L-34 5.5"
        stroke={SKIN}
        strokeWidth="4"
        strokeLinecap="round"
      />
      <ellipse cx="-30" cy="0" rx="12" ry="6" fill="#bfefff" opacity="0.45" />
      {[
        [-40, -5, 4],
        [-44, 1, 5],
        [-38, 6, 3.4],
        [-48, -2, 3],
        [-52, 4, 2.4],
      ].map(([x, y, r]) => (
        <circle
          key={`foam-${x}-${y}`}
          cx={x}
          cy={y}
          r={r}
          fill="#ffffff"
          opacity="0.8"
        />
      ))}
      {/* Torso */}
      <path
        d="M-16 -5 Q-2 -9 12 -7.5 Q16 0 12 7.5 Q-2 9 -16 5 Z"
        fill="#0d1d42"
      />
      <path
        d="M-14 -4.4 Q-2 -8 11 -6.6"
        stroke="#5b8cff"
        strokeOpacity="0.6"
        strokeWidth="1"
        fill="none"
      />
      {/* Extended arm */}
      <path
        d="M11 -6 L40 -8"
        stroke={SKIN}
        strokeWidth="3.6"
        strokeLinecap="round"
      />
      <ellipse cx="42" cy="-8" rx="3.4" ry="2.2" fill={SKIN} />
      {/* Recovering arm, elbow high */}
      <path
        d="M10 6.5 L-2 15 L14 19"
        stroke={SKIN}
        strokeWidth="3.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      <path
        d="M-2 15 L13 18.6"
        stroke="#ffe0c8"
        strokeOpacity="0.6"
        strokeWidth="1"
      />
      {[
        [0, 20, 3],
        [6, 22, 2.4],
        [-6, 18, 2],
      ].map(([x, y, r]) => (
        <circle
          key={`drip-${x}`}
          cx={x}
          cy={y}
          r={r}
          fill="#ffffff"
          opacity="0.75"
        />
      ))}
      {/* Head: cap + goggles, bow wave */}
      <path
        d="M30 -9 Q36 0 30 9"
        stroke="#ffffff"
        strokeOpacity="0.85"
        strokeWidth="2"
        fill="none"
      />
      <circle cx="21" cy="0" r="6" fill={cap} />
      <path
        d="M17 -4.6 Q21 0 17 4.6"
        stroke="#121212"
        strokeWidth="1.2"
        fill="none"
      />
      <ellipse
        cx="19"
        cy="-2.4"
        rx="2.4"
        ry="1.4"
        fill="#ffffff"
        opacity="0.55"
      />
    </g>
  );
}

function Swimmers() {
  const glints = [
    { x: 64, y: 58, delay: "0s" },
    { x: 236, y: 120, delay: "0.9s" },
    { x: 196, y: 40, delay: "1.7s" },
    { x: 30, y: 116, delay: "2.2s" },
  ];
  return (
    <svg
      className="absolute top-0 left-1/2 -translate-x-1/2"
      width="300"
      height="220"
      viewBox="0 0 300 220"
      aria-hidden="true"
      focusable="false"
    >
      <Swimmer transform="translate(150 76)" cap="#ff4d3d" />
      <Swimmer transform="translate(82 132) scale(0.9)" cap="#ffd23f" />
      {glints.map((glint) => (
        <path
          key={`glint-${glint.x}`}
          className="swim-glint"
          d={`M${glint.x} ${glint.y - 4} L${glint.x + 1} ${glint.y - 1} L${glint.x + 4} ${glint.y} L${glint.x + 1} ${glint.y + 1} L${glint.x} ${glint.y + 4} L${glint.x - 1} ${glint.y + 1} L${glint.x - 4} ${glint.y} L${glint.x - 1} ${glint.y - 1} Z`}
          fill="#ffffff"
          style={{ animationDelay: glint.delay }}
        />
      ))}
    </svg>
  );
}

export function SwimmingDisplayPreview({
  participants,
}: SwimmingDisplayPreviewProps) {
  const id = useSvgIds();

  return (
    <ActivityScenePreview
      label="Swimming"
      backgroundClassName="bg-sky-600"
      participants={participants}
      scene={
        <>
          <style href="swimming-scene-keyframes" precedence="default">
            {SWIM_KEYFRAMES}
          </style>
          <SceneDefs id={id} />

          {/* Sunlit water */}
          <div
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(60% 70% at 62% 30%, rgba(255,255,255,0.28) 0%, transparent 70%), linear-gradient(180deg, #2cd0ec 0%, #13a8d6 45%, #0a84ba 100%)",
            }}
          />
          <Pool id={id} />
          <Swimmers />
        </>
      }
      overlays={
        <div
          className="pointer-events-none absolute inset-0 z-10"
          style={{
            background:
              "radial-gradient(ellipse 120% 120% at 50% 40%, transparent 55%, rgba(2,40,70,0.45) 100%)",
          }}
        />
      }
    />
  );
}
